import { allowRole, ROLES } from './../../../_metronic/configs/role.config';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { User } from '../../../models/user.model';
import { AuthModel } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { DynamicAsideMenuConfig } from 'src/app/_metronic/configs/dynamic-aside-menu.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<User>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<User>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get isAdmin() {
    return this.currentUserValue.roleId === ROLES.ADMINISTRADOR
  }

  get isParent() {
    return this.currentUserValue.roleId === ROLES.APODERADO
  }

  get isProfessor() {
    return this.currentUserValue.roleId === ROLES.PROFESOR
  }

  get isVendedor() {
    return this.currentUserValue.roleId === ROLES.VENDEDOR;
  }
  get isRecepcionista() {
    return this.currentUserValue.roleId === ROLES.RECEPCIONISTA
  }

  get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  hasRole(roles: number[]): boolean {
    return roles.includes(this.currentUserValue?.roleId);
  }

  get isDirector() {
    return this.currentUserValue.roleId === ROLES.DIRECTOR
  }

  get isCoordinadorDeSede() {
    return this.currentUserValue.roleId === ROLES.COORDINADORDESEDE
  }

  get isGestorAcademico() {
    return this.currentUserValue.roleId === ROLES.GESTORACADEMICO
  }

  get isInstituto() {
    return this.currentUserValue.roleId === ROLES.INSTITUTO
  }
  get isStudent() {
    return this.currentUserValue.roleId === ROLES.ALUMNO;
  }
  get isAfiliado() {
    return this.currentUserValue?.roleId === ROLES.AFILIADO;
  }

  get isAuditor() {
    return this.currentUserValue?.roleId === ROLES.AUDITOR;
  }

  get isJefeContabilidad() {
    return this.currentUserValue?.roleId === ROLES.JEFECONTABILIDAD;
  }

  get isJefeVentas() {
    return this.currentUserValue?.roleId === ROLES.JEFEVENTAS;
  }

  get isCashFlowUser() {
    return this.isAuditor || this.isJefeContabilidad || this.isJefeVentas;
  }

  get canEditCashFlow() {
    const roleId = this.currentUserValue?.roleId;
    return roleId === ROLES.ADMINISTRADOR || roleId === ROLES.JEFECONTABILIDAD || roleId === ROLES.JEFEVENTAS;
  }

  get canViewCashFlowExpenses() {
    const roleId = this.currentUserValue?.roleId;
    return roleId === ROLES.ADMINISTRADOR || roleId === ROLES.JEFECONTABILIDAD || roleId === ROLES.AUDITOR;
  }

  set currentUserValue(user: User) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    const user: User = JSON.parse(localStorage.getItem('user'));
    this.currentUserSubject = new BehaviorSubject<User>(user);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  // public methods
  login(email: string, password: string): Observable<User> {
    localStorage.clear();
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      map((auth: User) => {
        const result = this.setAuthFromLocalStorage(auth);
        this.setUser(auth);
        return auth;
      }),
      // switchMap((e) => this.setUser(e)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // Federated login via Tesla.Identity SSO (legacy password grant)
  fedLogin(email: string, password: string): Observable<User> {
    localStorage.clear();
    this.isLoadingSubject.next(true);
    return this.authHttpService.fedLogin(email, password).pipe(
      map((auth: User) => {
        const result = this.setAuthFromLocalStorage(auth);
        this.setUser(auth);
        return auth;
      }),
      catchError((err) => {
        console.error('SSO login error:', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  /**
   * SSO estándar: Authorization Code + PKCE
   * El SPA recibe (?code=...) en /auth/callback y llama a este método con:
   * - code (del callback)
   * - codeVerifier (el que guardaste al iniciar authorize)
   * - redirectUri (exactamente el mismo que usaste en authorize)
   *
   * Backend (TeslaWebApi) canjea el code contra Tesla.Identity y te devuelve tu UserVM (con AuthToken).
   */
  oidcExchange(code: string, codeVerifier: string, redirectUri: string): Observable<User> {
    this.isLoadingSubject.next(true);

    // CAMBIO MÍNIMO: quitar el cast a "any" y llamar directo al método ya existente.
    return this.authHttpService.oidcExchange(code, codeVerifier, redirectUri).pipe(
      map((auth: User) => {
        const result = this.setAuthFromLocalStorage(auth);
        this.setUser(auth);
        return auth;
      }),
      catchError((err) => {
        console.error('OIDC exchange error:', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  /**
   * Logout global: limpia sesión local y redirige al IdP para logout completo
   * Solo para usar desde user-offcanvas (logout completo del sistema)
   */
  logoutGlobal() {
    // ✅ vuelve a fed-login (sin legacy) para que auto-redirija al IdP
    const returnTo = `${window.location.origin}/auth/login?returnUrl=/`;
    const wantsUrl = encodeURIComponent(returnTo);

    // ✅ NUEVO: identificar explícitamente quién inició el logout
    const initiator = encodeURIComponent('tesla_spa');

    // Limpiar localStorage (igual que logout() actual)
    localStorage.clear();

    // Limpiar sessionStorage (importante para claves temporales OIDC)
    sessionStorage.removeItem('oidc_pkce_verifier');
    sessionStorage.removeItem('oidc_state');
    sessionStorage.removeItem('oidc_nonce');
    sessionStorage.removeItem('oidc_redirect_uri');
    sessionStorage.removeItem('oidc_return_url');
    sessionStorage.removeItem('oidc_client_id');

    // Resetear subject de usuario actual
    this.currentUserSubject.next(undefined);

    const idpBaseUrl =
      (environment as any).identityBaseUrl || 'https://idp.preuteslaplus.com';

    window.location.href =
      `${idpBaseUrl.replace(/\/$/, '')}/account/logout?wantsurl=${wantsUrl}&initiator=${initiator}`;
  }

  /**
   * Logout simple: solo limpia sesión local y redirige al login
   * Sin comunicación con el IdP - para uso general en la aplicación
   */
  logout() {
    localStorage.clear();
    this.currentUserSubject.next(undefined);
    
    // Redirigir directamente al login sin pasar por el IdP
    this.router.navigate(['/auth/login']);
  }

/**
 * Limpia la sesión local (tokens/storage/memoria) sin redirigir al IdP
 * Utilizado para procesar logout remoto iniciado por el IdP
 */
/**
 * Limpia la sesión local (tokens/storage/memoria) sin redirigir al IdP
 * Utilizado para procesar logout remoto iniciado por el IdP
 */
  logoutLocalOnly() {
    // Limpiar localStorage (igual que logout() actual)
    localStorage.clear();

    // Limpiar sessionStorage (importante para claves temporales OIDC)
    sessionStorage.removeItem('oidc_pkce_verifier');
    sessionStorage.removeItem('oidc_state');
    sessionStorage.removeItem('oidc_nonce');
    sessionStorage.removeItem('oidc_redirect_uri');
    sessionStorage.removeItem('oidc_return_url');
    sessionStorage.removeItem('oidc_client_id');

    // Resetear subject de usuario actual
    this.currentUserSubject.next(undefined);
  }



  setUser(user, setToken = true) {
    this.currentUserSubject = new BehaviorSubject<User>(user);
    localStorage.setItem('user', JSON.stringify(user));
    if (setToken) {
      localStorage.setItem('expiresIn', user.expiresIn);
      localStorage.setItem('authToken', user.authToken);
      localStorage.setItem('version', environment.appVersion);
    }
  }

  getUserByToken(): Observable<User> {
    const auth = this.getAuthFromLocalStorage();
    return of(undefined);
    if (!auth || !auth.authToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.authToken).pipe(
      map((user: User) => {
        if (user) {
          this.currentUserSubject = new BehaviorSubject<User>(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string, password = 'preutesla', notify = true): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email, password, notify)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  resetPassword(email: string, password = 'preutesla', notify = true): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .resetPassword(email, password, notify)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  registration(user: User): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      // switchMap(() => this.login(user.userName, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  hasPermission(url: string) {
    let hasPermission = false;
    DynamicAsideMenuConfig.items.forEach((item) => {
      if (item.submenu) {
        item.submenu.forEach((subitem) => {
          if (url.indexOf(subitem.page) === 0
            &&
            allowRole(subitem.permission, this.currentUserValue.roleId)) {

            hasPermission = true;
          }
        });
      }
      else {
        if (url.indexOf(item.page) === 0
          &&
          allowRole(item.permission, this.currentUserValue.roleId)) {
          hasPermission = true;
        }
      }
    });
    return hasPermission;
  }

  setupSession(user: User) {
    // Set expiresIn (required by AuthGuard to validate the session)
    user.expiresIn = new Date(Date.now() + 24 * 60 * 60 * 1000);
    localStorage.clear();
    this.setAuthFromLocalStorage(user);
    this.setUser(user);
  }


  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.authToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel {
    try {
      const authData = JSON.parse(
        localStorage.getItem(this.authLocalStorageToken)
      );
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  getRecoveryUserByToken(token: string): Observable<any> {
    const expiresIn = new Date(Date.now() + 72 * 60 * 60 * 1000);
    this.setAuthFromLocalStorage({ authToken: token, refreshToken: '', expiresIn } as AuthModel);
    localStorage.setItem('authToken', token);
    this.currentUserSubject.next({ authToken: token } as User);

    return this.authHttpService.getRecoveryUserByToken(token).pipe(
      map((user: User) => {
        if (user) {
          user.authToken = token;
          user.expiresIn = expiresIn;
          return user;
        }
        return null;
      }),
      catchError((err) => {
        console.error('Error getting recovery user by token:', err);
        return of(null);
      })
    );
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
